import React, { useEffect, useState, useRef } from "react";
import { DataGrid, gridClasses, GridMoreVertIcon } from "@mui/x-data-grid";
import {
  styled,
  Typography,
  CircularProgress,
  IconButton,
  Popover,
  MenuItem,
} from "@mui/material";
import { HttpService } from "../../service/HttpService";
import { toast } from "react-toastify";
import { useAuth } from "../../context/AuthProvider";
import { CiEdit } from "react-icons/ci";
import { GoTrash } from "react-icons/go";
import { MdArchive, MdSave, MdClose } from "react-icons/md";
import { BiImport } from "react-icons/bi";
import COAImportModal from "./COAImportModal";
import DeleteModal from "../../components/Model/DeleteModal";
import UnmappedColumnsModal from "./UnmappedColumnsModal";
import { FALLBACK_KEYS } from "./COAFallbackKeys";
import "./COAReport.scss";

// ---------- INLINE EDIT COMPONENT ----------
const InlineEditInput = ({ initialValue, field, onChange }) => {
  const [val, setVal] = useState(initialValue);

  useEffect(() => {
    setVal(initialValue);
  }, [initialValue]);

  const handleChange = (e) => {
    setVal(e.target.value);
    onChange(field, e.target.value);
  };

  return (
    <input
      value={val}
      onChange={handleChange}
      onClick={(e) => e.stopPropagation()}
      onKeyDown={(e) => e.stopPropagation()}
      style={{
        width: "100%",
        padding: "8px",
        border: "1px solid #ccc",
        borderRadius: "4px",
        outline: "none",
        boxSizing: "border-box",
      }}
    />
  );
};

// ---------- FIELD CLASSIFICATION ----------
const BATCH_FIELDS = new Set([
  "batch_id",
  "order_number",
  "sample_number",
  "product_name",
  "strain",
  "matrix",
  "sampling_method",
  "order_date",
  "sampling_date",
  "receipt_date",
  "completion_date",
  "client_name",
  "client_license",
  "client_address_line1",
  "client_address_line2",
  "metrc_manifest",
  "metrc_source_tag",
  "metrc_sample_tag",
  "source_pdf",
  "microbials_status",
  "total_yeast_and_mold_status",
  "heavy_metals_status",
  "filth_and_foreign_material_status",
  "total_coliform_status",
  "pesticides_status",
  "total_terpenes",
  "extraction_warnings",
  "total_cannabinoids_percent",
  "total_thc_percent",
  "total_cbd_percent",
  "count_potency_analytes",
  "count_terpenes_analytes",
  "count_pesticides_analytes",
]);

const ANALYTE_FIELDS = new Set([
  "category",
  "analyte_name",
  "result_percent",
  "result_percent_text",
  "result_mg_g",
  "result_mg_g_text",
  "result_ppm",
  "result_ppm_text",
  "result_aw",
  "result_aw_text",
  "result",
  "result_text",
  "loq_percent",
  "loq_percent_text",
  "loq_ppm",
  "loq_ppm_text",
  "loq",
  "loq_text",
  "action_level_percent",
  "action_level_percent_text",
  "action_level_ppm",
  "action_level_ppm_text",
  "action_level_mg_g",
  "action_level_mg_g_text",
  "action_level_aw",
  "action_level_aw_text",
  "action_level",
  "action_level_text",
  "status",
]);

// ---------- EMPTY STATE ----------
const StyledGridOverlay = styled("div")(() => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  height: "100%",
}));

const COAReport = () => {
  const { setShowLoginPopup } = useAuth();
  const httpService = new HttpService(setShowLoginPopup);

  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [importModalOpen, setImportModalOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [menuRowId, setMenuRowId] = useState(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedBatchId, setSelectedBatchId] = useState(null);
  const [archiveModalOpen, setArchiveModalOpen] = useState(false);
  const [selectedArchiveBatchId, setSelectedArchiveBatchId] = useState(null);

  const [unmappedModalOpen, setUnmappedModalOpen] = useState(false);
  const [unmappedColumns, setUnmappedColumns] = useState([]);

  // ---------- INLINE EDIT STATE ----------
  const [editingRowId, setEditingRowId] = useState(null);
  const [originalValues, setOriginalValues] = useState({}); // snapshot on edit start

  const editValuesRef = useRef({}); // only changed fields
  const editingRowIdRef = useRef(editingRowId);
  const originalValuesRef = useRef(originalValues);
  const handleSaveEditRef = useRef(null);

  useEffect(() => {
    editingRowIdRef.current = editingRowId;
  }, [editingRowId]);

  useEffect(() => {
    originalValuesRef.current = originalValues;
  }, [originalValues]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      const currentId = editingRowIdRef.current;
      const currentValues = editValuesRef.current;
      const currentOriginalValues = originalValuesRef.current;

      if (!currentId) return;

      const isPopover = !!event.target.closest(
        ".MuiPopover-root, .MuiDialog-root, .MuiPickersPopper-root, .MuiPickersLayout-root",
      );
      if (isPopover) return;

      if (!document.body.contains(event.target)) return;

      const isInsideEditing = event.target.closest(
        `.MuiDataGrid-row[data-id="${currentId}"], input, textarea, select, .MuiSelect-select`,
      );

      if (!isInsideEditing) {
        if (!currentValues || Object.keys(currentValues).length === 0) {
          setEditingRowId(null);
          editValuesRef.current = {};
          setOriginalValues({});
        } else {
          if (handleSaveEditRef.current) {
            handleSaveEditRef.current(
              currentValues,
              currentOriginalValues,
              currentId,
            );
          }
        }
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // ---------- MENU HANDLERS ----------
  const handleMenuClick = (event, rowId) => {
    setAnchorEl(event.currentTarget);
    setMenuRowId(rowId);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
    setMenuRowId(null);
  };

  // ---------- EDIT HANDLERS ----------
  const handleEditStart = (row) => {
    setEditingRowId(row.id);
    setOriginalValues({ ...row });
    editValuesRef.current = {};
    handleCloseMenu();
  };

  const handleEditFieldChange = (field, value) => {
    editValuesRef.current[field] = value;
  };

  const handleCancelEdit = () => {
    setEditingRowId(null);
    editValuesRef.current = {};
    setOriginalValues({});
  };

  const handleSaveEdit = async (
    valuesToSave,
    currentOriginalValues,
    currentEditingId,
  ) => {
    // Determine the values to save. If not passed directly, use the ref.
    const vals =
      valuesToSave && Object.keys(valuesToSave).length > 0
        ? valuesToSave
        : editValuesRef.current;
    const origVals = currentOriginalValues || originalValues;

    if (Object.keys(vals).length === 0) {
      handleCancelEdit();
      return;
    }

    const batchId = origVals._batchId;

    const batchChanges = {};
    const analyteChangesRaw = {};

    Object.entries(vals).forEach(([field, value]) => {
      if (BATCH_FIELDS.has(field)) {
        batchChanges[field] = value;
      } else if (
        field !== "id" &&
        field !== "created_at" &&
        field !== "updated_at" &&
        field !== "_batchId" &&
        field !== "_analyteId" &&
        !field.endsWith("_id")
      ) {
        analyteChangesRaw[field] = value;
      }
    });

    const FIELD_TYPES = [
      "action_level_percent_text",
      "action_level_percent",
      "action_level_ppm_text",
      "action_level_ppm",
      "action_level_mg_g_text",
      "action_level_mg_g",
      "action_level_aw_text",
      "action_level_aw",
      "action_level_cfu_g",
      "action_level_text",
      "action_level",
      "result_percent_text",
      "result_percent",
      "result_mg_g_text",
      "result_mg_g",
      "result_ppm_text",
      "result_ppm",
      "result_aw_text",
      "result_aw",
      "result_cfu_g",
      "result_text",
      "result",
      "loq_percent_text",
      "loq_percent",
      "loq_ppm_text",
      "loq_ppm",
      "loq_cfu_g",
      "loq_text",
      "loq",
      "status",
      "percent",
      "mg_g",
    ];

    const groupedAnalytes = {};

    Object.entries(analyteChangesRaw).forEach(([field, value]) => {
      const fieldType = FIELD_TYPES.find((type) => field.endsWith(type));
      if (!fieldType) return;

      const prefix = field.replace(new RegExp(`_${fieldType}$`), "");
      const targetAnalyteId = origVals[`${prefix}_id`];

      if (targetAnalyteId) {
        if (!groupedAnalytes[targetAnalyteId]) {
          groupedAnalytes[targetAnalyteId] = { id: targetAnalyteId };
        }
        groupedAnalytes[targetAnalyteId][fieldType] = value;
      } else {
        if (!groupedAnalytes[prefix]) {
          const CATEGORIES = [
            "potency",
            "potency_summary",
            "terpenes_summary",
            "terpenes",
            "pesticides",
            "microbials",
            "heavy_metals",
            "yeast_mold",
            "water_activity",
            "filth_foreign",
            "total_coliform",
          ];
          const matchedCategory = CATEGORIES.find((c) => prefix.startsWith(c));
          const category = matchedCategory || "unknown";
          let analyte_name = prefix;
          if (matchedCategory) {
            analyte_name = prefix.replace(
              new RegExp(`^${matchedCategory}_`),
              "",
            );
          }
          groupedAnalytes[prefix] = { category, analyte_name };
        }
        groupedAnalytes[prefix][fieldType] = value;
      }
    });

    const payload = {};
    if (Object.keys(batchChanges).length > 0) payload.batch = batchChanges;

    const finalAnalytes = Object.values(groupedAnalytes);
    if (finalAnalytes.length > 0) {
      payload.analytes = finalAnalytes;
    }

    if (!payload.batch && !payload.analytes) {
      handleCancelEdit();
      return;
    }

    setLoading(true);
    try {
      const res = await httpService.put(`coa/edit/${batchId}`, payload);
      if (res?.success) {
        toast.success("Updated successfully");
        fetchData();
        handleCancelEdit();
      } else {
        toast.error(res?.message || "Update failed");
      }
    } catch (error) {
      console.error("Error updating COA record:", error);
      toast.error("Update failed");
    } finally {
      setLoading(false);
    }
  };

  handleSaveEditRef.current = handleSaveEdit;

  // ---------- DELETE ----------
  const handleDelete = async () => {
    if (!selectedBatchId) return;
    setLoading(true);
    try {
      const res = await httpService.delete(`coa/delete/${selectedBatchId}`);
      if (res?.success) {
        toast.success("Record deleted successfully");
        fetchData();
      } else {
        toast.error(res?.message || "Delete failed");
      }
    } catch (error) {
      console.error("Error deleting COA record:", error);
      toast.error("Delete failed");
    } finally {
      setLoading(false);
      setDeleteModalOpen(false);
      setSelectedBatchId(null);
    }
  };

  // ---------- ARCHIVE ----------
  const handleArchive = async () => {
    if (!selectedArchiveBatchId) return;
    setLoading(true);
    try {
      const res = await httpService.put(
        `coa/archive/${selectedArchiveBatchId}`,
      );
      if (res?.success) {
        toast.success("Record archived successfully");
        fetchData();
      } else {
        toast.error(res?.message || "Archive failed");
      }
    } catch (error) {
      console.error("Error archiving COA record:", error);
      toast.error("Archive failed");
    } finally {
      setLoading(false);
      setArchiveModalOpen(false);
      setSelectedArchiveBatchId(null);
    }
  };

  // ---------- IMPORT ----------
  const handleImport = async (file) => {
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await httpService.post("coa/import", formData);
      if (res?.success) {
        toast.success("File imported successfully");
        fetchData();
      } else {
        toast.error(res?.message || "Import failed");
      }
    } catch (error) {
      console.error("Error importing COA file:", error);
      const unmapped = error?.response?.data?.data?.unmappedColumns;
      if (unmapped && Array.isArray(unmapped) && unmapped.length > 0) {
        setUnmappedColumns(unmapped);
        setUnmappedModalOpen(true);
        fetchData();
        toast.success("File imported with unmapped columns");
      } else {
        toast.error("Import failed");
      }
    } finally {
      setLoading(false);
    }
  };

  // ---------- CATEGORY ORDERING ----------
  const CATEGORY_ORDER = {
    potency: 1,
    potency_summary: 2,
    microbials: 3,
    terpenes_summary: 4,
    heavy_metals: 5,
    yeast_mold: 6,
    water_activity: 7,
    filth_foreign: 8,
    terpenes: 9,
    total_coliform: 10,
    pesticides: 11,
    unknown: 12,
  };

  const getCategoryFromField = (fieldName) => {
    const categories = Object.keys(CATEGORY_ORDER);
    return categories.find((cat) => fieldName.startsWith(cat)) || "unknown";
  };

  // ---------- TRANSFORM DATA INTO ROWS ----------
  // Each row: batch fields + analyte fields flattened.
  // _batchId  → used in the URL for PATCH / DELETE / ARCHIVE
  // _analyteId → the coa_analytes.id, sent in analytes[] payload
  const transformData = (apiData) => {
    if (!apiData || !Array.isArray(apiData)) return [];

    const transformedData = apiData.map((item, index) => {
      const batchId = item?.batches?.id ?? index;
      const row = { id: batchId, _batchId: batchId };

      if (item?.batches) {
        Object.entries(item.batches).forEach(([k, v]) => {
          row[k] = v;
        });
        row._batchId = batchId; // re-set after spread (batches.id may have overwritten)
      }

      if (item?.analytes) {
        Object.entries(item.analytes).forEach(([k, v]) => {
          row[k] = v;
        });
      }

      return row;
    });

    // Sort by category order
    return transformedData.sort((a, b) => {
      // First sort by batch_id to keep batches together
      if (a.batch_id !== b.batch_id) {
        return (a.batch_id || "").localeCompare(b.batch_id || "");
      }

      // Then sort by category within each batch
      const aCategoryFields = Object.keys(a).filter(
        (key) =>
          !BATCH_FIELDS.has(key) &&
          key !== "id" &&
          key !== "_batchId" &&
          key !== "_analyteId" &&
          !key.endsWith("_id"),
      );
      const bCategoryFields = Object.keys(b).filter(
        (key) =>
          !BATCH_FIELDS.has(key) &&
          key !== "id" &&
          key !== "_batchId" &&
          key !== "_analyteId" &&
          !key.endsWith("_id"),
      );

      const aCategory =
        aCategoryFields.length > 0
          ? getCategoryFromField(aCategoryFields[0])
          : "unknown";
      const bCategory =
        bCategoryFields.length > 0
          ? getCategoryFromField(bCategoryFields[0])
          : "unknown";

      return (
        (CATEGORY_ORDER[aCategory] || 99) - (CATEGORY_ORDER[bCategory] || 99)
      );
    });
  };

  // ---------- COLUMNS (reactive to edit state) ----------
  const { allKeysArr } = React.useMemo(() => {
    // Start with batch fields first
    const batchKeys = Array.from(BATCH_FIELDS);
    const allAnalyteKeys = new Set();

    // Identify analyte keys that have at least one non-empty value in the current data
    if (data && data.length > 0) {
      data.forEach((row) => {
        Object.entries(row).forEach(([k, v]) => {
          // skip internal / meta fields and batch fields
          if (
            k === "id" ||
            k === "created_at" ||
            k === "updated_at" ||
            k === "_batchId" ||
            k === "_analyteId" ||
            k.endsWith("_id") ||
            k === "is_archived" ||
            k === "archived_at" ||
            BATCH_FIELDS.has(k)
          ) {
            return;
          }

          // Check if it has a real value (including ND, <LOQ which are strings)
          const hasValue =
            v !== null &&
            v !== undefined &&
            String(v).trim() !== "" &&
            String(v).trim() !== "-";

          if (hasValue) {
            // If it's a _text field, only add it if the base field or unit field doesn't exist
            if (k.endsWith("_text")) {
              const baseKey = k.replace(/_text$/, "");
              if (
                !BATCH_FIELDS.has(baseKey) &&
                !Object.keys(row).includes(baseKey)
              ) {
                allAnalyteKeys.add(k);
              }
            } else {
              allAnalyteKeys.add(k);
            }
          }
        });
      });
    }

    // Sort analyte keys by category
    const sortedAnalyteKeys = Array.from(allAnalyteKeys).sort((a, b) => {
      const aCategory = getCategoryFromField(a);
      const bCategory = getCategoryFromField(b);

      const aCategoryOrder = CATEGORY_ORDER[aCategory] || 99;
      const bCategoryOrder = CATEGORY_ORDER[bCategory] || 99;

      if (aCategoryOrder !== bCategoryOrder) {
        return aCategoryOrder - bCategoryOrder;
      }

      // Within same category, sort alphabetically
      return a.localeCompare(b);
    });

    // Combine batch fields first, then sorted analyte fields
    const allKeysOrdered = [...batchKeys, ...sortedAnalyteKeys];

    return { allKeysArr: allKeysOrdered };
  }, [data]);

  const maxLengths = React.useMemo(() => {
    const lengths = {};
    if (data && data.length > 0) {
      data.forEach((row) => {
        Object.entries(row).forEach(([k, v]) => {
          if (v !== null && v !== undefined) {
            const strLen = String(v).length;
            if (!lengths[k] || strLen > lengths[k]) {
              lengths[k] = strLen;
            }
          }
        });
      });
    }
    return lengths;
  }, [data]);

  const columns = React.useMemo(() => {
    if (allKeysArr.length === 0) return [];

    const isEditable = (key) =>
      key !== "id" &&
      key !== "created_at" &&
      key !== "updated_at" &&
      key !== "_batchId" &&
      key !== "_analyteId";

    return [
      // ---- ACTION COLUMN ----
      {
        field: "action",
        headerName: "Action",
        width: 120, // Match the 120px width from fake header CSS
        align: "center",
        headerAlign: "center",
        sortable: false,
        disableColumnMenu: true,
        renderCell: (params) => {
          const isEditing = editingRowId === params.row.id;

          if (isEditing) {
            return (
              <IconButton onClick={handleCancelEdit} color="error">
                <MdClose />
              </IconButton>
            );
          }

          return (
            <IconButton onClick={(e) => handleMenuClick(e, params.row.id)}>
              <GridMoreVertIcon />
            </IconButton>
          );
        },
      },

      // ---- DATA COLUMNS ----
      ...allKeysArr.map((key) => {
        const headerName = key
          .replace(/_text$/i, "")
          .replace(/_/g, " ")
          .replace(/\b\w/g, (char) => char.toUpperCase());

        // Add category prefix to header for better organization
        const category = getCategoryFromField(key);
        const categoryPrefix = BATCH_FIELDS.has(key)
          ? ""
          : category !== "unknown"
            ? `[${category.replace("_", " ").toUpperCase()}] `
            : "";
        const finalHeaderName = headerName;

        const maxContentLen = maxLengths[key] || 0;
        const headerLen = finalHeaderName.length;
        const maxLenToUse = Math.max(headerLen, maxContentLen);

        let dynamicMinWidth = Math.max(130, maxLenToUse * 8 + 40);

        const editable = isEditable(key);

        return {
          field: key,
          headerName: finalHeaderName,
          minWidth: dynamicMinWidth,
          flex: 1,
          renderCell: (params) => {
            const isEditing = editingRowId === params.row.id;

            // --- VALUE RESOLUTION (with limited fallbacks) ---
            let displayValue = params.value;
            if (
              displayValue === null ||
              displayValue === undefined ||
              displayValue === "" ||
              displayValue === "-" ||
              (typeof displayValue === "string" &&
                (displayValue.toLowerCase() === "nd" ||
                  displayValue.toLowerCase() === "<loq"))
            ) {
              const textFallbackKeys = [`${key}_text`];
              const prefixMatch = key.match(
                /^(.*)_(result|result_percent|result_mg_g|result_ppm|result_aw|percent|mg_g|loq|loq_percent|loq_ppm|action_level|action_level_percent|action_level_ppm|action_level_mg_g|action_level_aw)$/,
              );
              if (prefixMatch) {
                const prefix = prefixMatch[1];
                const suffix = prefixMatch[2];
                textFallbackKeys.push(`${prefix}_${suffix}_text`);

                // Only fall back to OTHER fields if we are NOT in a unit-specific column
                // or if we specifically want to check the basic _text version.
                if (
                  suffix === "result" ||
                  suffix === "percent" ||
                  suffix === "mg_g" ||
                  suffix === "aw"
                ) {
                  // If it's a unit-specific column, we primarily want EXACT matches or _text matches for that unit.
                  // We avoid falling back to other units (like mg_g -> percent) unless absolutely necessary.
                } else if (suffix.includes("loq")) {
                  textFallbackKeys.push(`${prefix}_loq_percent`);
                  textFallbackKeys.push(`${prefix}_loq_ppm`);
                  textFallbackKeys.push(`${prefix}_loq_text`);
                  textFallbackKeys.push(`${prefix}_loq_percent_text`);
                  textFallbackKeys.push(`${prefix}_loq_ppm_text`);
                } else if (suffix.includes("action_level")) {
                  textFallbackKeys.push(`${prefix}_action_level_percent`);
                  textFallbackKeys.push(`${prefix}_action_level_ppm`);
                  textFallbackKeys.push(`${prefix}_action_level_mg_g`);
                  textFallbackKeys.push(`${prefix}_action_level_aw`);
                  textFallbackKeys.push(`${prefix}_action_level_text`);
                  textFallbackKeys.push(`${prefix}_action_level_percent_text`);
                  textFallbackKeys.push(`${prefix}_action_level_ppm_text`);
                  textFallbackKeys.push(`${prefix}_action_level_mg_g_text`);
                  textFallbackKeys.push(`${prefix}_action_level_aw_text`);
                }
              }

              for (const fallbackKey of textFallbackKeys) {
                const fallbackVal = params.row[fallbackKey];
                if (
                  fallbackVal !== null &&
                  fallbackVal !== undefined &&
                  fallbackVal !== "" &&
                  fallbackVal !== "-" &&
                  !(
                    typeof fallbackVal === "string" &&
                    (fallbackVal.toLowerCase() === "nd" ||
                      fallbackVal.toLowerCase() === "<loq")
                  )
                ) {
                  displayValue = fallbackVal;
                  break;
                }
              }
            }

            // --- EDIT MODE ---
            if (isEditing && editable) {
              const currentVal = editValuesRef.current.hasOwnProperty(key)
                ? editValuesRef.current[key]
                : (displayValue ?? "");
              return (
                <InlineEditInput
                  initialValue={currentVal}
                  field={key}
                  onChange={handleEditFieldChange}
                />
              );
            }

            // --- READ MODE ---
            let val = displayValue;

            if (
              typeof val === "string" &&
              (val.toLowerCase() === "pass" || val.toLowerCase() === "fail") &&
              key.toLowerCase().includes("status")
            ) {
              const checkPass = val.toLowerCase() === "pass";
              return (
                <div
                  style={{
                    backgroundColor: checkPass ? "#e8f5e9" : "#ffebee",
                    color: checkPass ? "#2e7d32" : "#d32f2f",
                    padding: "4px 8px",
                    borderRadius: "4px",
                    textAlign: "center",
                    fontWeight: "bold",
                    width: "100%",
                  }}
                >
                  {val}
                </div>
              );
            }
            return val ?? "-";
          },
        };
      }),
    ];
  }, [allKeysArr, editingRowId]);

  // ---------- FETCH DATA ----------
  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await httpService.get("/coa");

      if (res?.data?.data) {
        const transformed = transformData(res.data.data);
        setData(transformed);
      }
    } catch (error) {
      console.error("Error fetching COA data:", error);
      toast.error("Failed to fetch COA data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const gridSx = React.useMemo(
    () => ({
      border: "1px solid #b3b3b3",
      [`& .${gridClasses.cell}`]: { py: 1 },
      "& .MuiDataGrid-columnHeaderTitle": {
        whiteSpace: "normal",
        lineHeight: "1.2",
        wordWrap: "break-word",
      },
      "& .MuiDataGrid-withBorderColor": {
        borderColor: "#b3b3b3",
      },
      "& .MuiDataGrid-row.editing-row": {
        backgroundColor: "#f0faf6",
      },
      "& .MuiDataGrid-row": {
        borderBottom: "1px solid #b3b3b3",
      },
      minHeight: data?.length > 0 ? 500 : 200,
    }),
    [data?.length],
  );

  const gridPinnedColumns = React.useMemo(
    () => ({
      left: ["action"],
    }),
    [],
  );

  const gridSlots = React.useMemo(
    () => ({
      noRowsOverlay: () => (
        <StyledGridOverlay>
          <Typography variant="body2" color="textSecondary">
            {loading ? <CircularProgress size={24} /> : "No Data Found"}
          </Typography>
        </StyledGridOverlay>
      ),
    }),
    [loading],
  );

  const getRowClassName = React.useCallback(
    (params) => (params.row.id === editingRowId ? "editing-row" : ""),
    [editingRowId],
  );

  return (
    <div className="coa-report">
      <div className="page-title pb-0 pt-4">
        <div className="title-wrap">
          <h1>COA</h1>
        </div>
        <div className="button-wrap">
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => setImportModalOpen(true)}
          >
            <BiImport /> Import
          </button>
        </div>
      </div>

      <div
        className="common_content frozen-table-wrapper"
        style={{
          // height: `${data?.length > 0 ? "" : "200px"}`,
          width: "100%",
          marginTop: 12,
        }}
      >
        {/* Fake frozen header */}
        <div
          className="fake-frozen-header"
          style={{ border: "1px solid #b3b3b3" }}
        >
          <div className="fake-header-cell" style={{ width: 120 }}>
            Action
          </div>
        </div>

        <DataGrid
          rows={data}
          columns={columns}
          loading={loading}
          disableRowSelectionOnClick
          getRowId={(row) => row.id}
          columnHeaderHeight={80}
          autoHeight
          sx={gridSx}
          getRowClassName={getRowClassName}
          disableVirtualization
          hideFooter
          showCellVerticalBorder
          showColumnVerticalBorder
          pinnedColumns={gridPinnedColumns}
          slots={gridSlots}
        />
      </div>

      <COAImportModal
        open={importModalOpen}
        onClose={() => setImportModalOpen(false)}
        onImport={handleImport}
      />
      <DeleteModal
        open={deleteModalOpen}
        HandleClose={() => {
          setDeleteModalOpen(false);
          setSelectedBatchId(null);
        }}
        HandleDelete={handleDelete}
      />
      <DeleteModal
        open={archiveModalOpen}
        HandleClose={() => {
          setArchiveModalOpen(false);
          setSelectedArchiveBatchId(null);
        }}
        HandleDelete={handleArchive}
      />
      <UnmappedColumnsModal
        open={unmappedModalOpen}
        columns={unmappedColumns}
        onClose={() => setUnmappedModalOpen(false)}
      />

      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleCloseMenu}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        sx={{
          "& .MuiPaper-root": {
            boxShadow: "0px 2px 8px rgba(0,0,0,0.15)",
            borderRadius: "8px",
          },
        }}
      >
        <MenuItem
          onClick={() => {
            const row = data.find((r) => r.id === menuRowId) || {
              id: menuRowId,
            };
            handleEditStart(row);
          }}
          sx={{
            display: "flex",
            justifyContent: "space-between",
            gap: 2,
            py: 1,
            px: 2,
          }}
        >
          <Typography variant="body2">Edit</Typography>
          <CiEdit size={18} />
        </MenuItem>
        <MenuItem
          onClick={() => {
            setSelectedArchiveBatchId(menuRowId);
            setArchiveModalOpen(true);
            handleCloseMenu();
          }}
          sx={{
            display: "flex",
            justifyContent: "space-between",
            gap: 2,
            py: 1,
            px: 2,
          }}
        >
          <Typography variant="body2">Archive</Typography>
          <MdArchive size={18} />
        </MenuItem>
        <MenuItem
          onClick={() => {
            setSelectedBatchId(menuRowId);
            setDeleteModalOpen(true);
            handleCloseMenu();
          }}
          sx={{
            display: "flex",
            justifyContent: "space-between",
            gap: 2,
            py: 1,
            px: 2,
          }}
        >
          <Typography variant="body2">Delete</Typography>
          <GoTrash size={18} />
        </MenuItem>
      </Popover>
    </div>
  );
};

export default COAReport;
